import React from 'react';
import {useSelector} from 'react-redux';
import {
    Dropdown,
    IDropdownOption,
    Icon,
    Stack,
    Text,
    TextField,
    Toggle,
    useTheme,
} from '@fluentui/react';
import NumberField from '../../Inputs/NumberField';
import MarkupTypeSelection from '../MarkupTypeSelection';
import {useGetGlobalStorefrontSettingsQuery} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {useGetOrganizationQuery} from '../../../store/endpoints/OrganizationsEndpoints';
import {RootState} from '../../../store/rootReducer';
import {IWizardSetupAnswers, setupValidationError} from './wizardSetupLogic';

/** Where the answers stand against the storefront record. Owned by the dialog, shown here. */
export type WizardSetupSaveState = 'unsaved' | 'queued' | 'saving' | 'saved';

interface IWizardSetupTabProps {
    answers: IWizardSetupAnswers;
    onChange: (answers: IWizardSetupAnswers) => void;
    saveState: WizardSetupSaveState;
}

const QUEUED_MESSAGE = 'The AI is still working on your storefront. Your setup will be saved automatically as soon as it pauses.';

/**
 * The four questions worth asking while the AI builds.
 *
 * Everything here is optional and everything here inherits: a question left alone keeps whatever the
 * organization's global storefront settings say, which is why the inherited value is shown as the
 * placeholder rather than pre-filled into the field. Pre-filling would turn "I did not answer this"
 * into "I chose exactly the default", and the two mean different things to the sparse patch these
 * answers are written with.
 *
 * There is no Save button: the dialog debounces a save of its own a moment after the last keystroke.
 * That makes the fields the only control on the step, so nothing here is disabled while a save runs
 * (typing into a field that goes dead every second and a half is worse than any race it prevents) and
 * the whole account of where the answers stand is the one muted line under them.
 */
const WizardSetupTab: React.FC<IWizardSetupTabProps> = (props) => {
    const theme = useTheme();
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const org = useGetOrganizationQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    const globalSettings = useGetGlobalStorefrontSettingsQuery();

    const {answers, onChange} = props;
    const quoteRequests = answers.allowRequestQuote === true;
    // One validator, two fields. Which field is at fault is decided here rather than by matching on the
    // message text, so the wording can change in one place without silently landing under the wrong label.
    const validation = setupValidationError(answers);
    const salesRepMissing = quoteRequests && (answers.defaultSalesRep ?? '').trim().length === 0;
    const salesRepError = salesRepMissing ? validation : undefined;
    const emailError = salesRepMissing ? undefined : validation;

    const repOptions: IDropdownOption[] = React.useMemo(
        () => (org.data?.users ?? []).map((user) => ({key: user.username ?? '', text: user.username ?? ''})),
        [org.data]
    );

    const globalMarkup = globalSettings.data?.defaultMarkup;
    const globalOrderEmail = globalSettings.data?.orderEmail;

    const set = (change: Partial<IWizardSetupAnswers>) => onChange({...answers, ...change});

    return (
        <Stack tokens={{childrenGap: '1em'}} styles={{root: {paddingTop: '1em'}}}>
            <Stack horizontal tokens={{childrenGap: '0.5em'}} verticalAlign='end'>
                <Stack.Item styles={{root: {width: 140}}}>
                    <NumberField
                        label='Default markup'
                        decimal
                        value={answers.defaultMarkup}
                        placeholder={`${globalMarkup ?? 'Not set'} (inherited)`}
                        onChange={(value) => set({defaultMarkup: value})}
                    />
                </Stack.Item>
                <Stack.Item styles={{root: {width: 160}}}>
                    <MarkupTypeSelection
                        label='Markup type'
                        value={answers.defaultMarkupType ?? ''}
                        onChange={(markupType) => set({defaultMarkupType: markupType})}
                    />
                </Stack.Item>
            </Stack>

            <Toggle
                label='Enable quote requests'
                onText='On'
                offText='Off'
                checked={quoteRequests}
                onChange={(_, checked) => set({allowRequestQuote: checked === true})}
            />

            <Dropdown
                label='Contact for quote requests'
                placeholder='Choose a user'
                options={repOptions}
                selectedKey={answers.defaultSalesRep ?? null}
                disabled={!quoteRequests}
                required={quoteRequests}
                errorMessage={salesRepError}
                onChange={(_, option) => set({defaultSalesRep: option?.key as string})}
            />

            <TextField
                label='Order confirmation BCC'
                value={answers.orderEmail ?? ''}
                placeholder={globalOrderEmail ? `${globalOrderEmail} (inherited)` : 'name@example.com'}
                errorMessage={emailError}
                onChange={(_, value) => set({orderEmail: value ?? ''})}
            />

            {/*
              * The whole save story, in one quiet line. It is deliberately small and grey: an autosave
              * that announced itself would be louder than the thing being saved. Untouched answers say
              * nothing at all, and a bad answer says nothing here either, because the field it belongs
              * to is already saying it.
              */}
            <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary, minHeight: 20}}}>
                {props.saveState === 'saving' && 'Saving...'}
                {props.saveState === 'queued' && QUEUED_MESSAGE}
                {props.saveState === 'saved' && (
                    <>
                        <Icon iconName='CheckMark' styles={{root: {marginRight: 4}}}/>
                        Saved
                    </>
                )}
            </Text>
        </Stack>
    );
};

export default WizardSetupTab;
